/**
 * Built once: `toLocaleString` with options constructs a fresh ICU formatter on every call, and a
 * 500-card pile formats several amounts per line on every render (TRO-2581).
 */
const MONEY_DIGITS = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a money amount for display. The API sends money as decimal strings such as "80.00". A
 * null or undefined amount (an unknown cost basis) renders as an em dash rather than "$0.00", which
 * would imply free. `available=false` applies the same rule to an aggregate zero produced when no
 * units could be priced.
 */
export function formatMoney(
  value: string | number | null | undefined,
  available = true,
): string {
  if (!available || value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return `$${MONEY_DIGITS.format(n)}`;
}

/** Format a public price in the currency the shop actually set. */
export function formatCurrency(
  value: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  if (!currency || currency === "USD") return formatMoney(value);
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  const code = currency.toUpperCase();
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${code} ${
      n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    }`;
  }
}

/** A shop-entered per-unit Market value wins until the field is cleared. */
export function effectiveMarketValue(
  override: string | null | undefined,
  providerMarket: string | number | null | undefined,
): string | null | undefined {
  return override?.trim()
    ? override
    : providerMarket == null
    ? providerMarket
    : String(providerMarket);
}

/** The accent a signed figure is rendered with; `default` is the untinted foreground. */
export type MoneyTone = "gain" | "loss" | "default";

/** The one text class per tone, so a tinted figure looks the same on every screen. */
export const MONEY_TONE_CLASS: Record<MoneyTone, string> = {
  gain: "text-gain",
  loss: "text-loss",
  default: "text-foreground",
};

/** A signed money figure resolved to its display string, tint, and spoken label. */
export type SignedMoney = {
  text: string;
  tone: MoneyTone;
  cls: string;
  a11y: string;
  up: boolean;
};

/**
 * Render a signed money figure. This is the one rendering of a signed amount, so the same quantity
 * looks the same on every screen. It enforces three rules:
 *
 * 1. Direction is carried exactly once: either the ▲/▼ glyph (`style: "glyph"`, the default) or a
 *    leading +/− sign (`style: "sign"`, for the compact deal rows), never both.
 * 2. The sign never lands inside the currency. `formatMoney(-628)` yields "$-628.00", so this
 *    formats the absolute value and puts the direction in front: "▼ $628.00" or "−$628.00".
 * 3. The tint comes from the same call as the text, so no call site can derive a direction from the
 *    raw field that disagrees with what it renders.
 *
 * The minus is U+2212 (−), not a hyphen, to match the weight of the ▲/▼ pair. `a11y` spells the
 * direction as a word, because a screen reader cannot voice ▲/▼ and the Honest Money Rule forbids
 * carrying direction by color alone. An unknown value (null, empty or non-finite) renders as an em
 * dash with no direction and no tint, because an unknown value is not a gain of zero. A zero
 * renders as the bare amount with no direction and no tint.
 */
export function signedMoney(
  value: string | number | null | undefined,
  style: "glyph" | "sign" = "glyph",
): SignedMoney {
  const n = typeof value === "string" ? Number(value) : value;
  if (value === "" || typeof n !== "number" || !Number.isFinite(n)) {
    return {
      text: "—",
      tone: "default",
      cls: MONEY_TONE_CLASS.default,
      a11y: "unavailable",
      up: false,
    };
  }
  const num = n;
  const amount = formatMoney(Math.abs(num));
  // Zero is no change: no glyph, sign or tint, so a flat day is not shown as a gain.
  if (num === 0) {
    return {
      text: amount,
      tone: "default",
      cls: MONEY_TONE_CLASS.default,
      a11y: "no change",
      up: true,
    };
  }
  const up = num > 0;
  const tone: MoneyTone = up ? "gain" : "loss";
  return {
    text: style === "glyph" ? `${up ? "▲" : "▼"} ${amount}` : `${up ? "+" : "−"}${amount}`,
    tone,
    cls: MONEY_TONE_CLASS[tone],
    a11y: `${up ? "up" : "down"} ${amount}`,
    up,
  };
}

/**
 * Render money that is a balance rather than a change, such as the Drawer (TRO-463).
 *
 * `signedMoney` does not fit: its ▲/▼ and gain green claim the amount went up, but a drawer
 * holding $845 did not gain $845. A non-negative balance renders in plain foreground with no
 * direction, like every other level in the app.
 *
 * A negative balance means the record says more cash left the bag than was ever in it, so it takes
 * the loss tint and a leading U+2212 minus, never the tint alone (DESIGN.md §6: gain and loss
 * always pair a hue with a sign). The sign stays outside the currency: "−$200.00", never
 * "$-200.00".
 *
 * An unknown value renders as an untinted em dash, because unknown is not zero.
 */
export function balanceMoney(value: string | number | null | undefined): SignedMoney {
  const n = typeof value === "string" ? Number(value) : value;
  if (value === "" || typeof n !== "number" || !Number.isFinite(n)) {
    return {
      text: "—",
      tone: "default",
      cls: MONEY_TONE_CLASS.default,
      a11y: "unavailable",
      up: false,
    };
  }
  const num = n;
  if (num < 0) {
    const amount = formatMoney(Math.abs(num));
    return {
      text: `−${amount}`,
      tone: "loss",
      cls: MONEY_TONE_CLASS.loss,
      a11y: `short ${amount}`,
      up: false,
    };
  }
  const text = formatMoney(num);
  return { text, tone: "default", cls: MONEY_TONE_CLASS.default, a11y: text, up: true };
}

/**
 * Format a percentage for a KPI tile as a whole percent. The API sends percents as decimal strings
 * such as "60.0". A null, undefined or empty value (no data yet, such as no sales) renders as an em
 * dash.
 */
export function formatPercent(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n)}%`;
}

/** Compact visual label for a provider's current listing spread. */
export function formatListedRange(
  low: string | number | null | undefined,
  high: string | number | null | undefined,
): string | null {
  if (low != null && high != null) return `Listed ${formatMoney(low)}–${formatMoney(high)}`;
  if (low != null) return `Low ${formatMoney(low)}`;
  if (high != null) return `High ${formatMoney(high)}`;
  return null;
}

/**
 * Parse a money string or number to integer cents, for example "12.5" → 1250 and "$4.20" → 420.
 * A bad, empty or non-finite value yields 0, never NaN, so callers can total fields without
 * guarding each one. Rounding to the nearest cent also absorbs float noise (0.29 × 100 =
 * 28.9999… → 29).
 */
export function toCents(value: string | number): number {
  const n = typeof value === "string" ? Number(value.replace(/[^0-9.-]/g, "")) : value;
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/**
 * Format integer cents as a two-decimal money string without a "$", for example 1234 → "12.34".
 * The inverse of `toCents` for whole-cent inputs; used to write redistributed amounts back into
 * the per-line price fields the screens submit.
 */
export function centsToStr(cents: number): string {
  return (Math.round(cents) / 100).toFixed(2);
}

/**
 * Parse an operator-typed total to a finite number, or null when the entry is empty or unparseable
 * ("", "abc", or the "4.3.0" some decimal pads allow). Null means "do not commit", so a garbage
 * value never reaches `toCents`, which would turn it into 0 and zero out the deal.
 */
export function parseTotal(value: string): number | null {
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (cleaned.trim() === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Group a two-decimal money string without a "$" with thousands separators, for example "1234.00"
 * → "1,234.00". Used to render large totals readably while the field is unfocused.
 */
export function groupThousands(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
